import React from "react";
import { Modal } from "../ui/modal";
import type { GuestProfile } from "../../types/guest";
import { UserCircleIcon, XMarkIcon } from "@heroicons/react/24/solid";

interface GuestSelectionGridModalProps {
  isOpen: boolean;
  onClose: () => void;
  guests: GuestProfile[];
  onGuestSelect: (guest: GuestProfile) => void;
  currentGuestId?: string;
  pagination?: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
  };
  onPageChange?: (page: number) => void;
  isLoading?: boolean;
}

const GuestSelectionGridModal: React.FC<GuestSelectionGridModalProps> = ({
  isOpen,
  onClose,
  guests,
  onGuestSelect,
  currentGuestId,
  pagination,
  onPageChange,
  isLoading = false,
}) => {
  if (!isOpen) return null;

  // Pagination handlers
  const handlePageChange = (newPage: number) => {
    if (
      pagination &&
      pagination.totalCount > 0 &&
      (newPage < 1 ||
        newPage > Math.ceil(pagination.totalCount / pagination.limit))
    ) {
      return;
    }
    onPageChange?.(newPage);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="max-w-3xl m-4"
      showCloseButton={false}
    >
      <div className="relative w-full max-w-3xl overflow-hidden rounded-2xl bg-white dark:bg-gray-800 shadow-xl">
        <div className="px-6 py-4 flex justify-between items-center">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-white">
            Select a Guest
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1 rounded-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
            aria-label="Close modal"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="border-b border-gray-200 dark:border-gray-700 mx-6"></div>

        {guests.length === 0 ? (
          <div className="p-10 text-center text-gray-500 dark:text-gray-400">
            <p>No guests available to select.</p>
          </div>
        ) : (
          <div className="p-6 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {guests.map((guest) => (
                <button
                  key={guest.guestId}
                  onClick={() => onGuestSelect(guest)}
                  disabled={guest.guestId === currentGuestId}
                  className={`
                    p-4 border rounded-lg text-left w-full
                    flex flex-col items-center justify-center
                    hover:bg-gray-50 dark:hover:bg-gray-700/50
                    focus:outline-none focus:ring-2 focus:ring-blue-500
                    transition-colors duration-150
                    ${
                      guest.guestId === currentGuestId
                        ? "bg-blue-100 dark:bg-blue-900/50 border-blue-300 dark:border-blue-700 cursor-default"
                        : "bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600"
                    }
                    disabled:opacity-70 disabled:cursor-not-allowed
                  `}
                >
                  <UserCircleIcon className="w-12 h-12 mb-2 text-gray-400 dark:text-gray-500" />
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate w-full text-center">
                    {guest.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Room: {guest.room.roomNumber}
                  </p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Pagination Controls */}
        {pagination && pagination.totalCount > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                  {Math.min(
                    pagination.page * pagination.limit,
                    pagination.totalCount
                  )}{" "}
                  of {pagination.totalCount} guests
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1 || isLoading}
                  className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-900 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Page {pagination.page} of{" "}
                  {Math.ceil(pagination.totalCount / pagination.limit) || 1}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={
                    pagination.page >=
                      Math.ceil(pagination.totalCount / pagination.limit) ||
                    isLoading
                  }
                  className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-900 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default GuestSelectionGridModal;
